import { useOptionalProjectId } from '../../contexts/ProjectIdContext'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type RefObject,
  type ReactNode,
} from 'react'
import { createPortal } from 'react-dom'
import { useNodes, useReactFlow, type Viewport } from 'reactflow'
import { Crosshair, X } from 'lucide-react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import type { ReactFlowCanvasNodeData } from '../../hooks/useYCanvas'
import { useBlockingOverlay } from '../../hooks/canvasShortcutGuard'
import { createNadDebug } from '../../utils/debugLog'
import {
  buildAssetCandidates,
  getNodeThumbnailPath,
  type CanvasAssetCandidate,
  type MediaType,
} from './nodes/shared/mentionUtils'
import type { SelectedReference } from './nodes/shared/ReferenceSelector'

const log = createNadDebug('dw:canvas-reference-picking')
export function referenceInteractionLog(event: {
  event: string
  owner_node_id?: string
  [key: string]: string | number | boolean | null | undefined
}) {
  log({
    ...event,
    feature: 'canvas-reference-picking',
    trace_id: `canvas-references:${event.project_id ?? 'local'}:${event.owner_node_id ?? 'unknown'}`,
  })
}

export interface CanvasReferencePickOptions {
  projectId?: string
  ownerNodeId: string
  references: SelectedReference[]
  onChange: (references: SelectedReference[]) => void
  mediaTypes: MediaType[]
  maxReferences?: number | null
  mediaLimits?: Partial<Record<MediaType, number>>
  referenceCandidateFilter?: (candidate: CanvasAssetCandidate) => boolean
}
interface PickSession {
  getOptions: () => CanvasReferencePickOptions
  viewport: Viewport
}
interface ReferenceInteractionContext {
  start: (getOptions: () => CanvasReferencePickOptions) => void
  focus: (ownerNodeId: string, sourceNodeId: string) => void
}
const Context = createContext<ReferenceInteractionContext | null>(null)
export const useReferenceInteractions = () => useContext(Context)

export function useCanvasReferencePicker(options: CanvasReferencePickOptions) {
  const context = useReferenceInteractions()
  const latest = useRef(options)
  latest.current = options
  return useCallback(() => context?.start(() => latest.current), [context])
}

/** Canvas-local interaction boundary. Node components keep their normal state
 * and the owner stays selected while capture handlers consume picking gestures. */
export function CanvasReferencePicking({ children }: { children: ReactNode }) {
  const projectId = useOptionalProjectId()
  const { t } = useTranslation('canvas')
  const flow = useReactFlow<ReactFlowCanvasNodeData>()
  const [session, setSession] = useState<PickSession | null>(null)
  const [returnTarget, setReturnTarget] = useState<{
    ownerNodeId: string
    viewport: Viewport
  } | null>(null)
  const start = useCallback(
    (getOptions: () => CanvasReferencePickOptions) => {
      const options = getOptions()
      setReturnTarget(null)
      referenceInteractionLog({
        event: 'pick:start',
        feature: 'canvas-reference-picking',
        project_id: options.projectId,
        owner_node_id: options.ownerNodeId,
        reference_count: options.references.length,
      })
      setSession({ getOptions, viewport: flow.getViewport() })
    },
    [flow],
  )
  const focus = useCallback(
    (ownerNodeId: string, sourceNodeId: string) => {
      if (!flow.getNode(sourceNodeId)) return
      const viewport = flow.getViewport()
      void flow.fitView({
        nodes: [{ id: sourceNodeId }],
        duration: 300,
        padding: 0.35,
        maxZoom: 1,
      })
      referenceInteractionLog({
        event: 'reference:focus',
        feature: 'canvas-reference-picking',
        project_id: projectId,
        owner_node_id: ownerNodeId,
        reference_node_id: sourceNodeId,
      })
      setReturnTarget({ ownerNodeId, viewport })
    },
    [flow, projectId],
  )
  const boundary = useRef<HTMLDivElement>(null)
  const context = useMemo(() => ({ start, focus }), [start, focus])
  return (
    <Context.Provider value={context}>
      <div ref={boundary} className="absolute inset-0">
        {children}
        {!session && returnTarget && (
          <ReferenceNavigationBanner
            testId="reference-return-banner"
            message={t('referenceInteraction.focused')}
            onReturn={() => {
              void flow.setViewport(returnTarget.viewport, { duration: 300 })
              referenceInteractionLog({
                event: 'reference:return',
                project_id: projectId,
                owner_node_id: returnTarget.ownerNodeId,
              })
              setReturnTarget(null)
            }}
            onClose={() => setReturnTarget(null)}
          />
        )}
        {session && (
          <ActivePicker
            session={session}
            onClose={() => setSession(null)}
            boundary={boundary}
          />
        )}
      </div>
    </Context.Provider>
  )
}

function ReferenceNavigationBanner({
  testId,
  message,
  onReturn,
  onClose,
}: {
  testId: string
  message: string
  onReturn: () => void
  onClose: () => void
}) {
  const { t } = useTranslation('canvas')
  return (
    <div
      role="status"
      className="absolute top-4 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 rounded-xl border border-primary/50 bg-surface-0 px-4 py-3 text-foreground shadow-xl"
      data-testid={testId}
    >
      <Crosshair className="w-4 h-4 text-primary" />
      <span className="text-base whitespace-nowrap">{message}</span>
      <button
        className="h-7 px-3 rounded-md border border-primary bg-primary text-sm font-medium text-primary-foreground transition-colors hover:border-primary/80 hover:bg-primary/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
        onClick={onReturn}
      >
        {t('referenceInteraction.return')}
      </button>
      <button
        aria-label={t('referenceInteraction.close')}
        className="rounded-lg p-1 hover:bg-surface-3 focus-visible:ring-2 focus-visible:ring-primary"
        onClick={onClose}
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  )
}

function ActivePicker({
  session,
  onClose,
  boundary,
}: {
  session: PickSession
  onClose: () => void
  boundary: RefObject<HTMLDivElement>
}) {
  const { t } = useTranslation('canvas')
  const flow = useReactFlow<ReactFlowCanvasNodeData>()
  const nodes = useNodes<ReactFlowCanvasNodeData>()
  const options = session.getOptions()
  const { mediaTypes, ownerNodeId, referenceCandidateFilter, references } = options
  const selected = useRef(references)
  const [selection, setSelection] = useState(references)
  const [hover, setHover] = useState<{
    id: string
    x: number
    y: number
  } | null>(null)
  useBlockingOverlay(true)
  const candidates = useMemo(() => {
    const assets = buildAssetCandidates(nodes, ownerNodeId).filter(
      (candidate) => (
        mediaTypes.includes(candidate.mediaType)
        && (referenceCandidateFilter?.(candidate) ?? true)
      ),
    )
    const result = new Map<string, SelectedReference>()
    for (const node of nodes) {
      const matches = assets.filter((candidate) => candidate.nodeId === node.id)
      const current =
        matches.find(
          (candidate) => candidate.assetPath === getNodeThumbnailPath(node),
        ) ?? matches[0]
      if (current) result.set(node.id, { ...current, source: 'canvas' })
    }
    return result
  }, [nodes, ownerNodeId, mediaTypes, referenceCandidateFilter])
  const finish = useCallback(
    (restore: boolean) => {
      if (restore) void flow.setViewport(session.viewport, { duration: 300 })
      referenceInteractionLog({
        event: 'pick:exit',
        feature: 'canvas-reference-picking',
        project_id: session.getOptions().projectId,
        owner_node_id: session.getOptions().ownerNodeId,
        restore_viewport: restore,
        reference_count: selected.current.length,
      })
      onClose()
    },
    [flow, onClose, session],
  )
  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      event.preventDefault()
      event.stopImmediatePropagation()
      finish(false)
    }
    document.addEventListener('keydown', handler, true)
    return () => document.removeEventListener('keydown', handler, true)
  }, [finish])
  useEffect(() => {
    selected.current = options.references
    setSelection(options.references)
  }, [options.references])
  const didFit = useRef(false)
  useEffect(() => {
    if (didFit.current) return
    didFit.current = true
    void flow.fitView({
      nodes: [
        { id: options.ownerNodeId },
        ...Array.from(candidates.keys(), (id) => ({ id })),
      ],
      padding: 0.2,
      duration: 350,
      maxZoom: Math.min(flow.getZoom(), 0.8),
    })
  }, [candidates, flow, options.ownerNodeId])
  useEffect(() => {
    if (!flow.getNode(options.ownerNodeId)) onClose()
    const element = boundary.current
    if (!element) return
    const marked = new Set<HTMLElement>()
    const markNodes = () => {
      for (const node of element.querySelectorAll<HTMLElement>(
        '.react-flow__node',
      )) {
        const id = node.dataset.id ?? ''
        node.classList.add('hover:outline-info')
        node.dataset.referencePick = candidates.has(id)
          ? 'eligible'
          : id === options.ownerNodeId
            ? 'owner'
            : 'ineligible'
        marked.add(node)
      }
    }
    markNodes()
    // Fit/zoom can mount previously culled nodes without changing node data.
    const observer = new MutationObserver(markNodes)
    observer.observe(element, { childList: true, subtree: true })
    return () => {
      observer.disconnect()
      for (const node of marked) {
        delete node.dataset.referencePick
        node.classList.remove('hover:outline-info')
      }
    }
  }, [boundary, candidates, flow, onClose, options.ownerNodeId])
  const nodeId = (target: EventTarget | null) =>
    target instanceof Element
      ? target.closest<HTMLElement>('.react-flow__node')?.dataset.id
      : undefined
  const toggle = (event: MouseEvent) => {
    const id = nodeId(event.target)
    if (!id) return
    event.stopPropagation()
    event.preventDefault()
    const candidate = candidates.get(id)
    if (!candidate) return
    const current = selected.current
    const removing = current.some((reference) => reference.nodeId === id)
    const mediaLimit = options.mediaLimits?.[candidate.mediaType]
    const limit =
      options.maxReferences != null && current.length >= options.maxReferences
        ? options.maxReferences
        : mediaLimit !== undefined &&
            current.filter(
              (reference) => reference.mediaType === candidate.mediaType,
            ).length >= mediaLimit
          ? mediaLimit
          : null
    if (!removing && limit !== null) {
      toast.error(
        t('referenceSelector.mediaReferenceLimitReached', { max: limit }),
        { id: 'canvas-reference-limit', position: 'top-center' },
      )
      referenceInteractionLog({
        event: 'pick:limit-refused',
        feature: 'canvas-reference-picking',
        project_id: options.projectId,
        owner_node_id: options.ownerNodeId,
        reference_node_id: id,
        media_type: candidate.mediaType,
        limit,
        reference_count: current.length,
      })
      return
    }
    const next = removing
      ? current.filter((reference) => reference.nodeId !== id)
      : [...current, candidate]
    options.onChange(next)
    selected.current = next
    setSelection(next)
    toast.dismiss('canvas-reference-limit')
    referenceInteractionLog({
      event: removing ? 'pick:deselect' : 'pick:select',
      feature: 'canvas-reference-picking',
      project_id: options.projectId,
      owner_node_id: options.ownerNodeId,
      reference_node_id: id,
      source_handle: candidate.sourceHandle,
      from_index: candidate.fromIndex,
      reference_count: next.length,
    })
  }
  useEffect(() => {
    const element = boundary.current
    if (!element) return
    const preventNodeGesture = (event: Event) => {
      if (nodeId(event.target)) {
        event.stopPropagation()
        event.preventDefault()
      }
    }
    const move = (event: MouseEvent) => {
      const id = nodeId(event.target)
      setHover(
        id && candidates.has(id)
          ? { id, x: event.clientX, y: event.clientY }
          : null,
      )
    }
    const leave = () => setHover(null)
    element.addEventListener('click', toggle, true)
    element.addEventListener('dblclick', preventNodeGesture, true)
    element.addEventListener('pointerdown', preventNodeGesture, true)
    element.addEventListener('mousedown', preventNodeGesture, true)
    element.addEventListener('mousemove', move, true)
    element.addEventListener('mouseleave', leave)
    return () => {
      element.removeEventListener('click', toggle, true)
      element.removeEventListener('dblclick', preventNodeGesture, true)
      element.removeEventListener('pointerdown', preventNodeGesture, true)
      element.removeEventListener('mousedown', preventNodeGesture, true)
      element.removeEventListener('mousemove', move, true)
      element.removeEventListener('mouseleave', leave)
    }
  })
  return (
    <div data-testid="canvas-reference-pick-mode">
      <ReferenceNavigationBanner
        testId="reference-pick-banner"
        message={t('referenceInteraction.pick')}
        onReturn={() => finish(true)}
        onClose={() => finish(false)}
      />
      {hover &&
        createPortal(
          <div
            className="fixed z-[9999] pointer-events-none rounded-full border border-border bg-surface-0 px-3 py-1 text-sm text-foreground shadow-lg"
            style={{
              left: Math.max(
                8,
                Math.min(hover.x + 14, window.innerWidth - 240),
              ),
              top: Math.min(hover.y + 16, window.innerHeight - 40),
            }}
            data-testid="reference-pick-cursor"
          >
            {selection.some((reference) => reference.nodeId === hover.id)
              ? t('referenceInteraction.deselect')
              : t('referenceInteraction.selectLabel', {
                  label: candidates.get(hover.id)?.label,
                })}
          </div>,
          document.body,
        )}
      <style>{`[data-reference-pick="ineligible"] { opacity: .3 !important; } [data-reference-pick="eligible"] { cursor: crosshair; } [data-reference-pick="eligible"]:hover { outline-style: solid; outline-width: 2px; outline-offset: 4px; }`}</style>
    </div>
  )
}
